import { Palette, Globe, Leaf, Box, Shield, Glasses, Atom, Rocket, Dna, Image } from 'lucide-react';
import type { Product } from './types';

export const emergingNicheProducts: Product[] = [
  {
    id: 'shapr3d-cad',
    title: 'Shapr3D',
    subtitle: 'iPad CAD Software',
    description: 'Professional CAD software designed for iPad and Mac with Apple Pencil compatibility, featuring smooth UI and live feedback for product teams.',
    icon: Palette,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#cad', '#design', '#ipad', '#product-design', '#3d-modeling'],
    features: [
      'Apple Pencil precision drawing',
      'Real-time collaboration',
      'Cloud synchronisation',
      'Professional CAD tools',
      'AR visualisation support',
      'Export to major CAD formats',
      'UK design standards compliance',
      'GDPR data protection'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'lokalise-translation',
    title: 'Lokalise',
    subtitle: 'App Translation Automation',
    description: 'Automated language translation platform for applications, trusted by companies including Starbucks, Hyundai, and Mastercard.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#localization', '#translation', '#internationalization', '#app-development', '#global'],
    features: [
      'AI-powered translation suggestions',
      'Developer-friendly API and integrations',
      'Translation memory and glossaries',
      'Quality assurance and review workflows',
      'Real-time collaboration with translators',
      'Support for 600+ file formats',
      'UK localisation standards',
      'GDPR translation data compliance'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'carbon-accounting-platform',
    title: 'Carbon Accounting Platform',
    subtitle: 'Emissions Management',
    description: 'Comprehensive carbon footprint measurement, reporting, and reduction platform for organisations committed to sustainability goals with UK environmental standards.',
    icon: Leaf,
    image: 'https://images.unsplash.com/photo-1569163139851-bf014377ab73?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#sustainability', '#carbon-accounting', '#esg', '#environmental', '#compliance'],
    features: [
      'Automated emissions calculation',
      'Supply chain carbon tracking',
      'ESG reporting and compliance',
      'Carbon offset marketplace integration',
      'Sustainability goal tracking',
      'Third-party verification support',
      'UK environmental standards compliance',
      'Government carbon reporting integration'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'digital-twin-management',
    title: 'Digital Twin Management',
    subtitle: 'Virtual Asset Replication',
    description: 'Creates and manages virtual replicas of physical assets, processes, or systems to enable advanced simulation and optimization.',
    icon: Box,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#digital-twin', '#iot', '#simulation', '#manufacturing', '#predictive-maintenance'],
    features: [
      'Real-time asset synchronization',
      'Predictive maintenance modeling',
      'Performance optimization simulation',
      'IoT sensor data integration',
      '3D visualization and modeling',
      'Historical trend analysis'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'blockchain-transaction-monitoring',
    title: 'Blockchain Transaction Monitoring',
    subtitle: 'Crypto Compliance Platform',
    description: 'Provides compliance and security monitoring for cryptocurrency transactions with advanced anomaly detection and regulatory reporting.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#blockchain', '#cryptocurrency', '#compliance', '#aml', '#fintech'],
    features: [
      'Real-time transaction monitoring',
      'AML/KYC compliance automation',
      'Risk scoring and alerts',
      'Regulatory reporting tools',
      'Multi-blockchain support',
      'Forensic investigation capabilities'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'metaverse-collaboration',
    title: 'Metaverse Collaboration Platform',
    subtitle: 'Virtual Reality Workspace',
    description: 'Enables teams to collaborate in immersive virtual environments with avatars, spatial audio, and interactive whiteboards.',
    icon: Glasses,
    image: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#metaverse', '#virtual-reality', '#collaboration', '#remote-work', '#immersive'],
    features: [
      'VR/AR meeting spaces',
      'Avatar customization and interaction',
      'Spatial audio and gesture recognition',
      'Virtual whiteboarding and presentations',
      'Cross-platform accessibility',
      'Meeting recording and playback'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'quantum-computing-simulation',
    title: 'Quantum Computing Simulation',
    subtitle: 'Quantum Algorithm Development',
    description: 'Allows researchers and developers to experiment with quantum algorithms and simulations without access to physical quantum computers.',
    icon: Atom,
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#quantum-computing', '#research', '#simulation', '#algorithms', '#scientific'],
    features: [
      'Quantum circuit design and simulation',
      'Algorithm optimization tools',
      'Educational quantum programming',
      'Collaboration with research institutions',
      'Performance benchmarking',
      'Integration with classical computing'
    ],
    pricing: { setup: 58000, monthly: 2668 },
    color: 'from-teal-500 to-green-500'
  },
  {
    id: 'space-operations-management',
    title: 'Space Operations Management',
    subtitle: 'Mission Planning Platform',
    description: 'Supports planning and execution of space missions with trajectory analysis, resource planning, and comprehensive risk assessment.',
    icon: Rocket,
    image: 'https://images.unsplash.com/photo-1446776877081-d282a0f896e2?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#space', '#aerospace', '#mission-planning', '#satellite', '#trajectory'],
    features: [
      'Mission trajectory planning',
      'Satellite constellation management',
      'Launch window optimization',
      'Ground station coordination',
      'Risk assessment and mitigation',
      'Real-time mission monitoring'
    ],
    pricing: { setup: 128000, monthly: 5888 },
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'bioinformatics-research',
    title: 'Bioinformatics Research Platform',
    subtitle: 'Genomic Data Analysis',
    description: 'Comprehensive platform for analyzing genomic data, running simulations, and collaborating on life science research projects.',
    icon: Dna,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#bioinformatics', '#genomics', '#research', '#life-sciences', '#healthcare'],
    features: [
      'Genomic sequence analysis',
      'Protein structure prediction',
      'Phylogenetic tree construction',
      'Drug discovery simulation',
      'Research collaboration tools',
      'Data visualization and interpretation'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'nft-management-platform',
    title: 'NFT Management Platform',
    subtitle: 'Digital Asset Marketplace',
    description: 'Comprehensive platform for creators and brands to mint, manage, and distribute NFTs with royalty tracking and detailed analytics.',
    icon: Image,
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=800&q=80',
    category: 'Emerging Technologies',
    status: 'Available',
    businessTypes: ['#nft', '#digital-assets', '#blockchain', '#creators', '#marketplace'],
    features: [
      'Easy NFT minting and deployment',
      'Royalty management and distribution',
      'Marketplace integration',
      'Creator analytics and insights',
      'Smart contract automation',
      'Community building tools'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-yellow-500 to-orange-500'
  }
];
